import { SMSPointOfService } from '@dh-io-emh/commerce-api/src/models/shared/ocb/api.schema';
import {
  IProductFilterPaginationDTO,
  IProductFilterValueDTO,
} from '../../models/product-filter/product-filter-base.model';
import ProductFilter, { IProductFilterType } from '../../models/product-filter/product-filter.model';
import { SortCode } from '../../models/sorting.model';
import { infuse } from '../../ioc-config';
import type { IDealerComplete, IDealerContext } from '../../models/dealer.model';
import Vehicle from '../../models/vehicle.model';
import { Logger } from '../../utils/logger.util';
import { SeamlessConnection, SeamlessCoreStore } from '@seamless/store';
import { AnyAction, Dispatch } from 'redux';
import { initializeStoreConnection, usePersistentStore } from '../store.util';

export enum LocationFilterTabs {
  Area,
  Dealer,
}

export enum ErrorType {
  NotFound = 'NOT_FOUND',
}

export type LocationFilterParams = {
  search: string;
  distance: string;
  id: string;
};

export type DealerInfo = {
  id: string;
  name: string;
  address: string;
};

export type Vendor = {
  productCode: string;
  pointOfServices: SMSPointOfService[];
};

export type FilterPayload = {
  filterCode: string;
  filterValue: any;
  filterMin?: number;
  filterMax?: number;
};

export type StorageItem<T> = {
  item: T;
  expires: number;
};

type GetState = () => { [connectionName]: ProductsState };

export type PublicProductsDispatchers = {
  loadDealerInfo: (products: Vehicle[]) => void;
  loadDealerInfoByCode: (code: string) => void;
  resetAllFilters: () => void;
  resetCategory: (codes: string[]) => void;
  resetPaging: () => void;
  setCategory: (category: string) => void;
  setCurrentPage: (page: number) => void;
  setDealerContext: (dealerContext: IDealerContext | null) => void;
  setUserContext: (userContext: string) => void;
  setDealerFilterInfo: (dealer?: DealerInfo) => void;
  setFilterValue: ({ filterCode, filterValue }: FilterPayload) => void;
  setLoading: (loading: boolean) => void;
  setLocationFilterParams: (params?: LocationFilterParams) => void;
  setLocationFilterTab: (tab?: LocationFilterTabs) => void;
  setSearchData: ({ results, filters, sortOptions, paging }: any) => void;
  setSortOrder: (sorting: string) => void;
  resetState: () => void;
};

export type AsyncProductsDispatchers = {
  getSearchProducts: (optional?: boolean) => Promise<void>;
  loadMoreResults: () => Promise<void>;
};

export type ProductsDispatchers = PublicProductsDispatchers & AsyncProductsDispatchers;

export type ProductsState = {
  dealerContext: IDealerContext | null;
  dealerFilterInfo: DealerInfo | null;
  dealersInfo: StorageItem<IDealerComplete>[];
  error: ErrorType | null;
  filters: IProductFilterType[];
  loadingProducts: string[];
  loadingResults: boolean;
  locationFilterParams: LocationFilterParams | null;
  locationFilterTab: LocationFilterTabs.Area;
  paging: IProductFilterPaginationDTO;
  results: Vehicle[];
  selectedCategory: string;
  selectedSort: string;
  sortOptions: IProductFilterValueDTO[];
  userContext: string | null;
  vendors: StorageItem<Vendor>[];
};

export const connectionName = 'EMH_PRODUCTS' as const;

export const getDefaultSortOrder = () => {
  const { defaultSortingOption } = infuse('config');

  return Object.values(SortCode).includes(defaultSortingOption as SortCode)
    ? defaultSortingOption
    : (SortCode.PriceAsc as const);
};

const initialStateParser = (state: ProductsState): ProductsState => ({
  ...state,
  dealersInfo: state.dealersInfo.filter((item) => item.expires > Date.now()),
  filters: state.filters.map((filter) => ProductFilter.create(filter)),
  error: null,
  results: state.results.map((vehicle) => Vehicle.create(vehicle)),
  vendors: state.vendors.filter((item) => item.expires > Date.now()),
});

const defaultPaging = {
  currentPage: 0,
  pageSize: 12,
  sort: '',
  totalPages: 0,
  totalResults: 0,
  totalResultsWithoutGrouping: 0,
} as IProductFilterPaginationDTO;

export const { initialState, updateState } = usePersistentStore<ProductsState>(
  connectionName,
  {
    dealerContext: null,
    dealerFilterInfo: null,
    dealersInfo: [],
    error: null,
    filters: [],
    loadingProducts: [],
    loadingResults: false,
    locationFilterParams: null,
    locationFilterTab: LocationFilterTabs.Area,
    paging: {
      ...defaultPaging,
    },
    results: [],
    selectedCategory: '',
    selectedSort: '',
    sortOptions: [],
    userContext: null,
    vendors: [],
  },
  initialStateParser,
);

// Store Action Names
const RESET_FILTERS = 'RESET_FILTERS';
const RESET_FILTERS_CATEGORY = 'RESET_FILTERS_CATEGORY';
const RESET_PAGING = 'RESET_PAGING';
const RESET_STATE = 'RESET_STATE';
const SET_CURRENT_PAGE = 'SET_CURRENT_PAGE';
const SET_ERROR = 'SET_ERROR';
const SET_FILTER_VALUE = 'SET_FILTER_VALUE';
const SET_LOADING = 'SET_LOADING';
const SET_SELECTED_CATEGORY = 'SET_SELECTED_CATEGORY';
const SET_SELECTED_SORT = 'SET_SELECTED_SORT';
const SET_LOCATION_FILTER_TAB = 'SET_LOCATION_FILTER_TAB';
const SET_LOCATION_FILTER_PARAMS = 'SET_LOCATION_FILTER_PARAMS';
const SET_DEALER_FILTER_INFO = 'SET_DEALER_FILTER_INFO';
const SET_DEALER_CONTEXT = 'SET_DEALER_CONTEXT';
const SET_USER_CONTEXT = 'SET_USER_CONTEXT';
const PUSH_VENDOR = 'PUSH_VENDOR';
const PUSH_DEALER_INFO = 'PUSH_DEALER_INFO';
const SET_DEALERS_INFO = 'SET_DEALERS_INFO';
const PUSH_LOADING_PRODUCT = 'PUSH_LOADING_PRODUCT';
const PULL_LOADING_PRODUCT = 'PULL_LOADING_PRODUCT';
const SET_SEARCH_DATA = 'SET_SEARCH_DATA';

export class ProductsConnection extends SeamlessConnection<PublicProductsDispatchers, ProductsState> {
  constructor() {
    super(connectionName);
  }

  get initialState(): ProductsState {
    return initialState;
  }

  getReducer() {
    return (state: ProductsState, action: AnyAction): ProductsState => {
      switch (action.type) {
        case this.getActionType(SET_SEARCH_DATA): {
          return updateState(state, {
            results: action.payload.results,
            filters: action.payload.filters,
            paging: action.payload.paging,
            sortOptions: action.payload.sortOptions,
            selectedSort:
              (action.payload.sortOptions?.find((sortOption) => sortOption.selected)?.code as string) ??
              SortCode.PriceAsc,
          });
        }
        case this.getActionType(SET_LOADING):
          return updateState(state, {
            loadingResults: action.payload,
          });
        case this.getActionType(RESET_FILTERS):
          return updateState(state, {
            filters: action.payload
              ? state.filters.map((filter) => {
                  const clonedFilter = filter.clone();

                  clonedFilter.value = clonedFilter.getResetValue();

                  return clonedFilter;
                })
              : [],
            paging: { ...defaultPaging },
            dealerFilterInfo: null,
            locationFilterParams: null,
          });
        case this.getActionType(RESET_FILTERS_CATEGORY):
          return updateState(state, {
            filters: action.payload
              ? state.filters.map((filter) => {
                  if (action.payload.includes(filter.code)) {
                    const clonedFilter = filter.clone();

                    clonedFilter.value = clonedFilter.getResetValue();

                    return clonedFilter;
                  }

                  return filter;
                })
              : [],
            paging: { ...defaultPaging },
            dealerFilterInfo: null,
            locationFilterParams: null,
          });
        case this.getActionType(RESET_PAGING):
          return updateState(state, {
            paging: { ...defaultPaging },
          });
        case this.getActionType(RESET_STATE):
          return { ...initialState };
        case this.getActionType(SET_CURRENT_PAGE):
          return updateState(state, {
            paging: {
              ...state.paging,
              currentPage: action.payload,
            },
          });
        case this.getActionType(SET_FILTER_VALUE): {
          return updateState(state, {
            filters: state.filters.map((filter) => {
              if (filter.code !== action.payload.filterCode) return filter;

              const selectedFilter = filter.clone();

              selectedFilter.value = action.payload.filterValue;
              selectedFilter.max = action.payload.filterMax ?? selectedFilter.max;
              selectedFilter.min = action.payload.filterMin ?? selectedFilter.min;

              return selectedFilter;
            }),
            paging: { ...defaultPaging },
          });
        }
        case this.getActionType(SET_SELECTED_SORT):
          return updateState(state, {
            selectedSort: action.payload,
            paging: {
              ...state.paging,
              currentPage: 0,
            },
          });
        case this.getActionType(SET_DEALER_CONTEXT):
          return updateState(state, {
            dealerContext: action.payload,
          });
        case this.getActionType(SET_USER_CONTEXT):
          return updateState(state, {
            userContext: action.payload,
          });
        case this.getActionType(SET_SELECTED_CATEGORY):
          return updateState(state, {
            selectedCategory: action.payload,
          });
        case this.getActionType(SET_LOCATION_FILTER_TAB):
          return updateState(state, {
            locationFilterTab: action.payload,
          });
        case this.getActionType(SET_DEALER_FILTER_INFO):
          return updateState(state, {
            dealerFilterInfo: action.payload,
          });
        case this.getActionType(SET_LOCATION_FILTER_PARAMS):
          return updateState(state, {
            locationFilterParams: action.payload,
          });
        case this.getActionType(PUSH_VENDOR):
          return updateState(state, {
            vendors: !state.vendors.some((vendor) => vendor.item.productCode === action.payload.item.productCode)
              ? [...state.vendors, action.payload]
              : state.vendors,
          });
        case this.getActionType(PUSH_DEALER_INFO): {
          const infos: string[] = state.dealersInfo.map((info) => info.item.baseInfo.externalId);

          return updateState(state, {
            dealersInfo: action.payload.filter((info) => !infos.includes(info.item.baseInfo.externalId)).length
              ? [...state.dealersInfo, ...action.payload]
              : state.dealersInfo,
          });
        }
        case this.getActionType(SET_DEALERS_INFO):
          return updateState(state, {
            dealersInfo: action.payload,
          });
        case this.getActionType(PUSH_LOADING_PRODUCT):
          return updateState(state, {
            loadingProducts: [...state.loadingProducts, action.payload],
          });
        case this.getActionType(PULL_LOADING_PRODUCT):
          return updateState(state, {
            loadingProducts: state.loadingProducts.filter(
              (loadingProductCode) => loadingProductCode !== action.payload,
            ),
          });
        case this.getActionType(SET_ERROR):
          return {
            ...state,
            error: action.payload,
          };
        default:
          return state;
      }
    };
  }

  getPublicDispatchers() {
    return {
      setSearchData:
        ({ results, filters, sortOptions, paging }) =>
        (dispatch: Dispatch) => {
          dispatch(this.getAction(SET_SEARCH_DATA, { results, filters, sortOptions, paging }));
        },
      setLoading: (loading: boolean) => (dispatch: Dispatch) => {
        dispatch(this.getAction(SET_LOADING, loading));
      },
      setCurrentPage: (page: number) => (dispatch: Dispatch) => {
        dispatch(this.getAction(SET_CURRENT_PAGE, page));
      },
      loadDealerInfo: (vehicles: Vehicle[]) => async () => {
        try {
          const { dealerLocator } = infuse('config');

          // Check if additional logic for the dealer name and address is needed in the current market
          if (!dealerLocator.enableEnhancedName && !dealerLocator.enableEnhancedAddress) return;

          vehicles.forEach((vehicle) => productsDispatchers.loadDealerInfoByCode(vehicle.code));
        } catch (error) {
          Logger.error('ProductsConnection::loadDealerInfo()', error);
        }
      },
      loadDealerInfoByCode: (productCode: string) => async (dispatch: Dispatch, getState: GetState) => {
        const state = getState()[connectionName];

        try {
          // Set loading state to the product
          dispatch(this.getAction(PUSH_LOADING_PRODUCT, productCode));

          const { dealerLocator, lang } = infuse('config');
          const productApi = infuse('productApi');
          const dealerApi = infuse('dealerApi');

          const pointOfServices: SMSPointOfService[] = [];
          const storedVendor = state.vendors.find((vendor) => vendor.item.productCode === productCode);

          // Get point of services from the store or get them from the DLC API
          if (storedVendor) {
            pointOfServices.push(...storedVendor.item.pointOfServices);
          } else {
            const vendor = await productApi.getVendorForProduct(productCode);

            pointOfServices.push(...vendor.pointOfServices);

            // Add new item to the store
            dispatch(
              this.getAction(PUSH_VENDOR, {
                item: {
                  productCode,
                  pointOfServices: vendor.pointOfServices,
                },
                // Expires in 15 minutes
                expires: new Date(Date.now() + 15 * 60 * 1000).getTime(),
              }),
            );
          }

          // Find which point of services don't have complete information
          const missingInfos: SMSPointOfService[] = pointOfServices.filter(
            (pos) => !state.dealersInfo.some((info) => pos.name === info.item.baseInfo.externalId),
          );

          // Check if some dealers are missing detailed information
          if (missingInfos.length) {
            const dealerInfos = await dealerApi.getDealersById(
              missingInfos.map((pos) => pos.name) as string[],
              dealerLocator.languagesForLocalizedData?.includes(lang),
            );

            // Add new complete infos for dealers into the store
            if (dealerInfos.length) {
              dispatch(
                this.getAction(
                  PUSH_DEALER_INFO,
                  dealerInfos.map((info) => ({
                    item: info,
                    // Expires in 1 day
                    expires: new Date(Date.now() + 24 * 60 * 60 * 1000).getTime(),
                  })),
                ),
              );
            }
          }

          // Remove loading state from the product
          dispatch(this.getAction(PULL_LOADING_PRODUCT, productCode));
        } catch (error) {
          Logger.error('ProductsConnection::loadDealerInfoByCode()', error);
        }
      },
      resetAllFilters: () => (dispatch: Dispatch) => {
        try {
          dispatch(this.getAction(RESET_FILTERS, true));
        } catch (error) {
          Logger.error('ProductsConnection::resetAllFilters()', error);
        }
      },
      resetCategory: (codes: string[]) => (dispatch: Dispatch) => {
        try {
          dispatch(this.getAction(RESET_FILTERS_CATEGORY, codes));
        } catch (error) {
          Logger.error('ProductsConnection::resetAllFilters()', error);
        }
      },
      resetPaging: () => (dispatch: Dispatch) => {
        try {
          dispatch(this.getAction(RESET_PAGING));
        } catch (error) {
          Logger.error('ProductsConnection::setLocationFilterParams()', error);
        }
      },
      setCategory: (category: string) => (dispatch: Dispatch) => {
        try {
          dispatch(this.getAction(SET_SELECTED_CATEGORY, category));
        } catch (error) {
          Logger.error('ProductsConnection::setCategory()', error);
        }
      },
      setDealerContext: (dealerContext: IDealerContext | null) => (dispatch: Dispatch) => {
        try {
          dispatch(this.getAction(SET_DEALER_CONTEXT, dealerContext));
        } catch (error) {
          Logger.error('ProductsConnection::setDealerContext()', error);
        }
      },
      setUserContext: (userContext?: string) => (dispatch: Dispatch) => {
        try {
          dispatch(this.getAction(SET_USER_CONTEXT, userContext ?? null));
        } catch (error) {
          Logger.error('ProductsConnection::setUserContext()', error);
        }
      },
      setDealerFilterInfo: (dealer?: DealerInfo) => (dispatch: Dispatch) => {
        try {
          dispatch(this.getAction(SET_DEALER_FILTER_INFO, dealer ?? null));
        } catch (error) {
          Logger.error('ProductsConnection::setDealerFilterInfo()', error);
        }
      },
      setFilterValue:
        ({ filterCode, filterValue, filterMin, filterMax }: FilterPayload) =>
        async (dispatch: Dispatch) => {
          try {
            dispatch(this.getAction(SET_FILTER_VALUE, { filterCode, filterValue, filterMin, filterMax }));
          } catch (error) {
            Logger.error('ProductsConnection::setFilterValue()', error);
          }
        },
      setLocationFilterParams: (params?: LocationFilterParams) => (dispatch: Dispatch) => {
        try {
          dispatch(this.getAction(SET_LOCATION_FILTER_PARAMS, params ?? null));
        } catch (error) {
          Logger.error('ProductsConnection::setLocationFilterParams()', error);
        }
      },
      setLocationFilterTab: (tab?: LocationFilterTabs) => (dispatch: Dispatch) => {
        try {
          dispatch(this.getAction(SET_LOCATION_FILTER_TAB, tab ?? null));
        } catch (error) {
          Logger.error('ProductsConnection::setLocationFilterTab()', error);
        }
      },
      setSortOrder: (sorting: string) => (dispatch: Dispatch) => {
        try {
          dispatch(this.getAction(SET_SELECTED_SORT, sorting));
        } catch (error) {
          Logger.error('ProductsConnection::setSortOrder()', error);
        }
      },
      resetState: () => (dispatch: Dispatch) => {
        dispatch(this.getAction(RESET_STATE));
      },
    };
  }
}

const asyncDispatchers: AsyncProductsDispatchers = {
  getSearchProducts: async (optional?: boolean): Promise<void> => {
    const state = await getState();

    if (!state.selectedCategory && !optional) {
      throw new Error('ProductsActions::getSearchProducts() -> Missing mandatory category parameter.');
    }

    Logger.debug('ProductsActions::getSearchProducts() -> fetching products');
    productsDispatchers.setLoading(true);

    try {
      const productApi = infuse('productApi');

      const response = await productApi.searchProducts({
        selectedSort: state.selectedSort || getDefaultSortOrder(),
        selectedCategory: state.selectedCategory,
        filters: state.filters,
        paging: state.paging,
        dealerContext: state.dealerContext ?? undefined,
        userContext: state.userContext ?? undefined,
      });

      productsDispatchers.setSearchData({
        // In case of paging append results, replace otherwise
        results: state.paging.currentPage === 0 ? response.products : [...state.results, ...response.products],
        filters: response.filters,
        sortOptions: response.sorts,
        paging: response.pagination,
      });

      productsDispatchers.loadDealerInfo(response.products);
    } catch (error) {
      Logger.error('ProductsActions::getSearchProducts() -> failed to fetch products', error);
    } finally {
      productsDispatchers.setLoading(false);
    }
  },
  loadMoreResults: async (): Promise<void> => {
    const state = await getState();

    if (state.paging.currentPage + 1 >= state.paging.totalPages) {
      throw new Error('ProductsActions::loadMoreResult() -> Cannot load more results.');
    }

    productsDispatchers.setCurrentPage(state.paging.currentPage + 1);
    await productsDispatchers.getSearchProducts();
  },
};

export let productsStore: SeamlessCoreStore;
export let productsDispatchers: ProductsDispatchers;

export const initializeProductsStore = async () => {
  productsStore = (await initializeStoreConnection<ProductsState, PublicProductsDispatchers>(
    ProductsConnection,
  )) as SeamlessCoreStore;
  productsDispatchers = {
    ...(await productsStore.getConnectionDispatchers<PublicProductsDispatchers>(connectionName)),
    ...asyncDispatchers,
  };
};

export const getState = (): Promise<ProductsState> =>
  new Promise((resolve) => productsStore.once(connectionName, resolve));
